import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { Bookmark, ReaderLayout } from '../types/api';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useIsAboveReaderBreakpoint } from '../components/Reader/useReaderBreakpoint';
import { getReaderIdsFromPathname } from '../utils/readerRouteUtils';
import { useBookmarkSelection } from './BookmarkSelectionContext';

export type ActiveBookmark = { id: string; highlightId: string | null } | null;

export interface BookmarkPageContextValue {
  bookmarks: Bookmark[];
  activeBookmark: ActiveBookmark;
  readerLayout: ReaderLayout;
  setActiveBookmark: (
    bookmark: {
      id: string;
      highlightId?: string | null;
    } | null,
  ) => void;
}

const BookmarkPageContext = createContext<BookmarkPageContextValue | undefined>(
  undefined,
);

/**
 * Reader id and highlight id from the URL when on a list+read route
 * (e.g. /read/123, /folder/x/read/123, /read/123/456).
 * BookmarkPageProvider is only used in list views.
 */
function useReaderIdsFromRoute(): {
  readerId: string | null;
  highlightId: string | null;
} {
  const { pathname } = useLocation();
  const params = useParams({ strict: false });
  return getReaderIdsFromPathname(pathname, params);
}

export function BookmarkPageProvider({
  children,
  bookmarks,
}: {
  children: ReactNode;
  bookmarks: Bookmark[];
}) {
  const { readerBookmarkId, setReaderBookmarkId, readerLayoutMode } =
    useUserPreferences();
  const { isBatchMode } = useBookmarkSelection();
  const isAboveBreakpoint = useIsAboveReaderBreakpoint();
  const { readerId: readerBookmarkIdFromRoute, highlightId: readerHighlightIdFromRoute } =
    useReaderIdsFromRoute();

  // When route provides a reader id (e.g. /folder/x/read/123, /read/123/456), use it; otherwise use preference
  // In 2col mode, only use route-based reader ID (not preference) so navigating back clears the reader
  const effectiveReaderId =
    readerLayoutMode === '2col'
      ? readerBookmarkIdFromRoute
      : (readerBookmarkIdFromRoute ?? readerBookmarkId);
  const effectiveHighlightId = readerHighlightIdFromRoute;

  // Preference can request 2col; below 1200px force 2col unless user chose full or read/:id in URL (show reader then)
  // In 2col mode: if article selected, show full-width reader; otherwise show list only
  let readerLayout: ReaderLayout;
  if (readerLayoutMode === '2col') {
    // In 2col mode: if article selected, show full-width reader; otherwise list only
    readerLayout = effectiveReaderId != null ? 'full' : '2col';
  } else if (readerLayoutMode === '3col' && (isBatchMode || isAboveBreakpoint)) {
    readerLayout = '3col';
  } else if (!effectiveReaderId) {
    readerLayout = '2col';
  } else if (
    !isAboveBreakpoint &&
    readerBookmarkIdFromRoute == null &&
    readerLayoutMode !== 'full'
  ) {
    readerLayout = '2col';
  } else if (!isAboveBreakpoint && readerBookmarkIdFromRoute != null) {
    readerLayout = 'full';
  } else if (readerLayoutMode === 'full') {
    readerLayout = 'full';
  } else {
    readerLayout = '3col';
  }

  // In 2col mode, activeBookmark is only set when article is selected from URL (not from preference)
  // This ensures navigating back clears the reader view
  const activeBookmark = useMemo<ActiveBookmark>(
    () =>
      effectiveReaderId != null
        ? { id: effectiveReaderId, highlightId: effectiveHighlightId }
        : null,
    [effectiveReaderId, effectiveHighlightId],
  );

  const setActiveBookmark = useCallback(
    (bookmark: { id: string; highlightId?: string | null } | null) => {
      setReaderBookmarkId(bookmark?.id ?? null);
    },
    [setReaderBookmarkId],
  );

  // Sync URL to preferences: persist when reading, clear when navigating away
  useEffect(() => {
    if (readerBookmarkIdFromRoute != null) {
      setReaderBookmarkId(readerBookmarkIdFromRoute);
    } else {
      // No reader in URL (e.g. navigated to a folder/section) — clear saved bookmark
      // so the placeholder fan animation shows instead of a stale article
      setReaderBookmarkId(null);
    }
  }, [readerBookmarkIdFromRoute, setReaderBookmarkId]);

  // Set exactly one reader layout body class from resolved readerLayout
  // Map new values to CSS body classes (which remain unchanged for semantic clarity)
  useEffect(() => {
    document.body.classList.remove(
      'read-three-column',
      'read-full-width',
      'read-list-only',
    );
    if (readerLayout === 'full') {
      document.body.classList.add('read-full-width');
    } else if (readerLayout === '3col') {
      document.body.classList.add('read-three-column');
    } else {
      document.body.classList.add('read-list-only');
    }
  }, [readerLayout]);

  const value = useMemo(
    () => ({ bookmarks, activeBookmark, readerLayout, setActiveBookmark }),
    [bookmarks, activeBookmark, readerLayout, setActiveBookmark],
  );

  return (
    <BookmarkPageContext.Provider value={value}>{children}</BookmarkPageContext.Provider>
  );
}

export function useBookmarkPage() {
  const context = useContext(BookmarkPageContext);
  if (!context) {
    throw new Error('useBookmarkPage must be used within BookmarkPageProvider');
  }
  return context;
}

// Returns the bookmark page context when inside BookmarkPageProvider, otherwise undefined
export function useBookmarkPageContent() {
  return useContext(BookmarkPageContext);
}
